import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@components/ui/dialog";
import { Controller, useForm } from "react-hook-form";
import QuillEditor from "../../../../../components/CreateQuestion/Question/QuillEditor";
import dayjs from "@lib/dayjs";
import { useSnackbar } from "notistack";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import SkillItem from "../experienceBlock/skillItem";
import { SkillInput } from "../../skill/skillInput";
import FormControl from "../../../../../components/FormControl/FormControl";
import { experienceFeilds } from "../experienceBlock/experienceBlock.types";
import { SkillData } from "../../skill/skillInput.types";
import { X } from "lucide-react";
import { JobSkills } from "@utils/constants";
import { addExperience, updateExperience } from "@endpoints/experience.service";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import store from "@store/index";
import { useDispatch } from "react-redux";
import { setUserData } from "@store/actions/auth.action";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
const months = [
  { name: "January", value: 1 },
  { name: "February", value: 2 },
  { name: "March", value: 3 },
  { name: "April", value: 4 },
  { name: "May", value: 5 },
  { name: "June", value: 6 },
  { name: "July", value: 7 },
  { name: "August", value: 8 },
  { name: "September", value: 9 },
  { name: "October", value: 10 },
  { name: "November", value: 11 },
  { name: "December", value: 12 },
];

export interface IExperience {
  _id: string;
  candidateId: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  jobTitle: string;
  companyName: string;
  location: string;
  jobDescription: string;
  skills: {
    title: string;
    rating: number;
    years: number;
    lastUsed: number;
  }[];
  createdAt: string;
  updatedAt: string;
}

interface AddEditExperienceModalProps {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  experience?: IExperience;
}

const AddEditExperienceModal: React.FC<AddEditExperienceModalProps> = ({
  showModal,
  setShowModal,
  experience,
}) => {
  const dispatch = useDispatch();
  const isEditMode = Boolean(experience?._id);

  const defaultValues = {
    _id: undefined,
    startDate: undefined,
    startYear: undefined,
    startMonth: undefined,
    endYear: undefined,
    endMonth: undefined,
    endDate: undefined,
    jobTitle: undefined,
    companyName: undefined,
    location: undefined,
    jobDescription: undefined,
    isCurrent: false,
    isNewAdded: false,
    isLoadingDelete: false,
    skills: [],
  };
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors, touchedFields },
    watch,
  } = useForm<experienceFeilds>({
    defaultValues: defaultValues,
  });
  const fields = watch();

  const { enqueueSnackbar } = useSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skillError, setSkillError] = useState<string | null>(null);

  useEffect(() => {
    if (!showModal) return;

    if (experience?._id) {
      const start = experience.startDate ? dayjs(experience.startDate) : null;
      const end = experience.endDate ? dayjs(experience.endDate) : null;

      reset({
        ...defaultValues,
        _id: experience._id,
        // @ts-ignore — defaults type for startDate is `undefined`, but we need the actual string here
        startDate: experience.startDate,
        startYear: start ? start.year() : undefined,
        startMonth: start ? start.month() + 1 : undefined,
        // @ts-ignore
        endDate: experience.endDate,
        endYear: end && !experience.isCurrent ? end.year() : undefined,
        endMonth: end && !experience.isCurrent ? end.month() + 1 : undefined,
        // @ts-ignore
        jobTitle: experience.jobTitle,
        // @ts-ignore
        companyName: experience.companyName,
        // @ts-ignore
        location: experience.location,
        // @ts-ignore
        jobDescription: experience.jobDescription,
        isCurrent: !!experience.isCurrent,
        skills: experience.skills?.map((s, idx) => ({ ...s, _id: idx })) ?? [],
      });
    } else {
      reset(defaultValues);
    }
  }, [showModal, experience?._id]);

  useEffect(() => {
    if (fields.startDate) {
      const start = dayjs(fields.startDate);
      setValue("startYear", start.year());
      setValue("startMonth", start.month() + 1);
    }

    if (fields.endDate && !fields.isCurrent) {
      const end = dayjs(fields.endDate);
      setValue("endYear", end.year());
      setValue("endMonth", end.month() + 1);
    } else if (fields.isCurrent) {
      setValue("endYear", undefined);
      setValue("endMonth", undefined);
    }
  }, [fields.startDate, fields.endDate, fields.isCurrent, setValue]);

  const closeAndReset = () => {
    reset(defaultValues);
    setSkillError(null);
    setShowModal(false);
  };

  const handleAddSkill = (skill: SkillData) => {
    let skillArray = [...fields?.skills];

    if (skill?._id !== undefined) {
      skillArray[skill?._id] = skill;
    } else {
      skillArray.unshift({ ...skill, _id: 0 });
    }
    setValue("skills", skillArray);
    setSkillError(null);
  };

  const handleDeleteSkill = (_id: number) => {
    if (fields?.skills) {
      const updatedSkills = fields.skills.filter((_, index) => index !== _id);

      setValue("skills", []);

      setTimeout(() => {
        setValue("skills", updatedSkills);
      }, 0);
    }
  };

  const handleStartDateChange = (year?: number, month?: number) => {
    if (year && month) {
      const newStartDate = dayjs(`${year}-${month}-01`).format("YYYY-MM-DD");
      //@ts-ignore
      setValue("startDate", newStartDate);
    }
  };

  const handleEndDateChange = (year?: number, month?: number) => {
    if (year && month && !fields.isCurrent) {
      const newEndDate = dayjs(`${year}-${month}-01`).format("YYYY-MM-DD");
      //@ts-ignore
      setValue("endDate", newEndDate);
    }
  };

  const handleSaveExperience = async (data: experienceFeilds) => {
    console.log("this is experience form", data);

    if (!data?.skills || data.skills.length === 0) {
      setSkillError("Please add at least one skill.");
      return;
    }

    try {
      setIsSubmitting(true);

      const formatDate = (year?: string, month?: string) => {
        if (!year || !month) return undefined; // Ensure both values exist
        return dayjs
          .utc(`${year}-${month}-27`)
          .format("YYYY-MM-DDT00:00:00.000[Z]");
      };

      const payload = {
        ...data,
        startDate: formatDate(data.startYear, data.startMonth),
        endDate: data.isCurrent
          ? undefined
          : formatDate(data.endYear, data.endMonth),
        skills: data?.skills?.map((skill) => ({
          ...skill,
          lastUsed:
            typeof skill?.lastUsed === "string"
              ? JobSkills.find(
                  (j) =>
                    j.label?.toLowerCase() ===
                    String(skill?.lastUsed)?.toLowerCase(),
                )?.value
              : skill?.lastUsed,
        })),
      };

      if (payload._id) {
        await updateExperience({ ...payload, id: payload?._id }).then(
          (response) => {
            if (response.success) {
              const experiences = store.getState().auth.user?.experiences || [];
              console.log("updated experiences", experiences);
              const updatedExperiences = experiences?.map((exp) => {
                return exp?._id === response?.data?._id ? response?.data : exp;
              });
              console.log("updated experiences2", updatedExperiences,response?.data);

              dispatch(setUserData({ experiences: updatedExperiences }));
              enqueueSnackbar("Experience updated successfully.", {
                variant: "success",
              });
              closeAndReset();
            }
          },
        );
      } else {
        await addExperience(payload).then((response) => {
          if (response.success) {
            const experiences = store.getState().auth.user?.experiences || [];
            dispatch(
              setUserData({ experiences: [...experiences, response?.data] }),
            );
            enqueueSnackbar("Experience added successfully.", {
              variant: "success",
            });
            closeAndReset();
          }
        });
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={showModal} onOpenChange={(open) => !open && closeAndReset()}>
      <DialogContent
        showCloseButton={false}
        className="bg-white !max-w-4xl max-h-[96%] overflow-hidden grid-rows-[minmax(0,1fr)]"
      >
        {/* Wrapper */}
        <div className="flex flex-col flex-1 gap-5">
          {/* Header */}
          <div className="flex items-center justify-between w-full">
            <span className="font-bold text-lg">
              {isEditMode ? "Edit experience" : "Add experience"}
            </span>
            <DialogClose asChild>
              <button onClick={closeAndReset}>
                <X size={25} />
              </button>
            </DialogClose>
          </div>
          {/* Body */}
          <div className="flex flex-col flex-1 min-h-0 overflow-y-scroll no-scrollbar">
            <div className="flex flex-col gap-4 mb-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={fields?.isCurrent}
                  className="accent-primary size-4 rounded text-emerald-500"
                  onChange={(e) => {
                    if (
                      fields?.startDate &&
                      dayjs(fields.startDate).isSameOrAfter(dayjs(), "date")
                    ) {
                      enqueueSnackbar(
                        "Start date should be ahead of current date",
                        {
                          variant: "error",
                        },
                      );
                      return;
                    }
                    setValue("isCurrent", e?.target?.checked);
                  }}
                />
                <label className="text-charcoal text-xs font-medium">
                  Currently working for this role
                </label>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="text-charcoal font-medium text-xs mb-2">
                      Start Date
                    </label>
                    <div className="flex gap-2">
                      <Controller
                        name="startMonth"
                        control={control}
                        rules={{ required: "This field is required." }}
                        render={({ field }) => (
                          <select
                            {...field}
                            className="border rounded-lg px-3 py-2.5 w-full text-xs"
                          >
                            <option value="">Month</option>
                            {months
                              .filter(
                                (m) =>
                                  !(
                                    fields.startYear == currentYear &&
                                    m.value > currentMonth
                                  ),
                              )
                              .map((month) => (
                                <option key={month.value} value={month.value}>
                                  {month.name}
                                </option>
                              ))}
                          </select>
                        )}
                      />

                      {/* Start Year */}
                      <Controller
                        name="startYear"
                        control={control}
                        rules={{ required: "This field is required." }}
                        render={({ field }) => (
                          <select
                            {...field}
                            className="border rounded-lg px-3 py-2.5 w-full text-xs cursor-pointer"
                            onChange={(e) => {
                              field.onChange(Number(e.target.value));
                              handleStartDateChange(
                                Number(e.target.value),
                                fields.startMonth,
                              );
                            }}
                          >
                            <option value="">Year</option>
                            {years.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                    </div>
                  </div>
                  <FormControl
                    label={"Job Title"}
                    name={"jobTitle"}
                    placeholder={"Enter your Job Title"}
                    rules={{ required: "This field is required." }}
                    control={control}
                    errors={errors}
                  />
                  <FormControl
                    label="Location"
                    name="location"
                    rules={{
                      required: "This field is required.",
                    }}
                    placeholder={"Ex: London"}
                    control={control}
                    errors={errors}
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <div
                    className={`${fields?.isCurrent ? "opacity-50" : "opacity-100"}`}
                  >
                    <label className="text-charcoal font-medium text-xs mb-2">
                      End Date
                    </label>
                    <div className="flex gap-2">
                      <Controller
                        name="endMonth"
                        control={control}
                        rules={{
                          required: fields?.isCurrent
                            ? false
                            : "Month is required",
                        }}
                        render={({ field }) => (
                          <select
                            {...field}
                            className="border rounded-lg px-3 py-2.5 w-full text-xs"
                            disabled={fields?.isCurrent}
                          >
                            <option value="">Month</option>
                            {months
                              .filter(
                                (m) =>
                                  !(
                                    fields.endYear == currentYear &&
                                    m.value > currentMonth
                                  ),
                              )
                              .map((month) => (
                                <option key={month.value} value={month.value}>
                                  {month.name}
                                </option>
                              ))}
                          </select>
                        )}
                      />

                      {/* End Year */}
                      <Controller
                        name="endYear"
                        control={control}
                        rules={{
                          required: fields?.isCurrent
                            ? false
                            : "Year is required",
                        }}
                        render={({ field }) => (
                          <select
                            {...field}
                            className="border rounded-lg px-3 py-2.5 w-full text-xs cursor-pointer"
                            disabled={fields?.isCurrent}
                            onChange={(e) => {
                              field.onChange(Number(e.target.value));
                              handleEndDateChange(
                                Number(e.target.value),
                                fields.endMonth,
                              );
                            }}
                          >
                            <option value="">Year</option>
                            {years.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                    </div>
                  </div>

                  <FormControl
                    label={"Company Name"}
                    name={"companyName"}
                    placeholder={"Enter your Company Name"}
                    rules={{ required: "This field is required." }}
                    control={control}
                    errors={errors}
                  />
                </div>
              </div>
              <div className="mr-4">
                <Controller
                  name={"jobDescription"}
                  control={control}
                  rules={{
                    required: "This field is required.",
                  }}
                  render={({ field }) => {
                    return (
                      <div className="flex flex-col gap-1">
                        <span className="font-montserrat text-charcoal font-medium text-xs mb-2">
                          Job Description{" "}
                          <span className="font-montserrat font-medium text-xs text-red-500">
                            *
                          </span>
                        </span>
                        <QuillEditor
                          onChange={field.onChange}
                          value={field.value}
                          label=""
                          required={true}
                        />
                        {errors["jobDescription"]?.message && (
                          <span className="whitespace-pre text-xs mt-2 text-orange-700">
                            {errors["jobDescription"]?.message}
                          </span>
                        )}
                      </div>
                    );
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col px-16 gap-3">
              <div className="flex items-center justify-between mt-7 mb-2 px-4">
                <span className="text-xs font-semibold text-gray-700">
                  Skills <span className="text-red-500">*</span>
                </span>
                {skillError && (
                  <span className="text-xs text-orange-700">{skillError}</span>
                )}
              </div>
              <div className="grid flex-1 px-4 justify-center grid-cols-[1.9fr_0.75fr_0.6fr_1fr_0.5fr]">
                {["Skill", "Rating", "Years", "When", ""].map((label) => (
                  <span
                    key={label}
                    className="text-xs font-semibold text-gray-700 flex-1"
                  >
                    {label}
                  </span>
                ))}
              </div>
              <SkillInput
                data={{
                  lastUsed: "",
                  rating: "",
                  title: "",
                  years: "",
                  _id: undefined,
                  isEdit: true,
                  isSaved: false,
                }}
                addUpdateSkill={handleAddSkill}
                index={undefined}
                deleteSkill={handleDeleteSkill}
              />
              <div className="h-10" />
              {fields?.skills?.length > 0 && (
                <div className="flex flex-col gap-3">
                  {fields?.skills.map((skill: any, index: number) => (
                    <SkillItem
                      key={skill?._id}
                      skillData={skill}
                      index={index}
                      showEditDeleteButtons
                      handleAddSkill={handleAddSkill}
                      handleDeleteSkill={handleDeleteSkill}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex items-center gap-5">
            <button
              type="button"
              onClick={closeAndReset}
              className="text-sm text-red-600 font-semibold"
            >
              Cancel
            </button>
            <button
              disabled={isSubmitting}
              onClick={handleSubmit(handleSaveExperience)}
              className="flex items-center gap-2 bg-primary rounded-md text-white hover:bg-primary/90 font-semibold text-sm py-2 px-5"
            >
              {isEditMode ? "Update" : "Submit"}
              {isSubmitting ? <LoadingAnimation /> : null}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditExperienceModal;
